import Phaser from "phaser";
import { Enemy, DamageType } from "./Enemy";
import { arrowTrail, explosion, frostBurst, magicImpact, pierceTrail, poisonCloud } from "../assets/effects";
import { impact } from "../systems/Impact";
import { WALK_FRAME_SUFFIXES } from "../config";

export type ProjectileBehavior = "homing" | "splash" | "pierce";

export interface ProjectileOpts {
  textureKey: string;
  target: Enemy;
  speed: number;
  damage: number;
  /** True when the firing tower rolled a critical hit — drives the big number. */
  crit?: boolean;
  damageType: DamageType;
  behavior: ProjectileBehavior;
  /** Required for splash. AoE radius. */
  splashRadius?: number;
  /** Pierce: how many enemies the bolt may hit before dying. */
  pierceCount?: number;
  /** Source tower key — used for DoT stacking. */
  sourceKey: string;
  /** AoE-on-hit debuffs (frost, poison). Applied to enemies inside splashRadius. */
  applySlow?: { factor: number; durationMs: number };
  applyDot?: { dps: number; durationMs: number };
  /** Effect to play at impact point. */
  impactEffect?: "explosion" | "frost" | "poison" | "none";
  /** Live enemy list — used for splash/pierce hit detection. */
  enemiesGetter: () => Enemy[];
}

export class Projectile extends Phaser.GameObjects.Image {
  public alive = true;

  // Pierce: track enemies already hit so we don't damage them twice.
  private hit = new Set<Enemy>();
  // Pierce: aimed flight direction (locked at spawn).
  private dirX = 1;
  private dirY = 0;
  private maxTravel = 1200;
  private traveled = 0;
  private trailTimerMs = 0;

  private opts: ProjectileOpts;
  public logicalX: number;
  public logicalY: number;
  private startDist = 1;

  /**
   * Spin frames, or a single entry for projectiles that do not tumble (arrow,
   * bolt — they are rotated to face the target, so a spin would be wrong).
   */
  private readonly frames: string[];
  private frameIdx = 0;
  private ageMs = 0;
  /**
   * One full turn per ~210 ms. Tuned against a real shot: a cannon firing at a
   * target two tiles away is in the air ~150 ms, so a slower spin would only
   * ever show the first half of the cycle before the ball landed.
   */
  private static readonly FRAME_MS = 35;

  constructor(scene: Phaser.Scene, x: number, y: number, opts: ProjectileOpts) {
    super(scene, x, y, opts.textureKey);
    this.opts = opts;
    this.logicalX = x;
    this.logicalY = y;
    if (opts.target) {
      this.startDist = Math.hypot(opts.target.x - x, opts.target.y - y) || 1;
    }

    // Take spin frames while they exist — generateTextures registers them only
    // for the round projectiles.
    this.frames = [opts.textureKey];
    for (const suffix of WALK_FRAME_SUFFIXES.slice(1)) {
      if (!scene.textures.exists(`${opts.textureKey}${suffix}`)) break;
      this.frames.push(`${opts.textureKey}${suffix}`);
    }

    scene.add.existing(this);
    this.setDepth(y + 50);

    // Face the target on the spawn frame as well; otherwise a right-facing
    // source texture can flash in the wrong direction for one rendered frame.
    if (opts.target) {
      this.rotation = Math.atan2(opts.target.y - y, opts.target.x - x);
    }

    if (opts.behavior === "pierce") {
      const dx = opts.target.x - x;
      const dy = opts.target.y - y;
      const d = Math.hypot(dx, dy) || 1;
      this.dirX = dx / d;
      this.dirY = dy / d;
      this.rotation = Math.atan2(dy, dx);
    }
  }

  update(_time: number, deltaMs: number): void {
    if (!this.alive) return;
    const dt = deltaMs / 1000;
    this.trailTimerMs += deltaMs;
    const opts = this.opts;

    // Tumble. Driven by the scene's speed-scaled delta like everything else, so
    // the spin keeps pace at 2x and 4x.
    if (this.frames.length > 1) {
      this.ageMs += deltaMs;
      const idx = Math.floor(this.ageMs / Projectile.FRAME_MS) % this.frames.length;
      if (idx !== this.frameIdx) {
        this.frameIdx = idx;
        this.setTexture(this.frames[idx]);
      }
    }

    const lastX = this.x;
    const lastY = this.y;

    if (opts.behavior === "pierce") {
      const step = opts.speed * dt;
      this.logicalX += this.dirX * step;
      this.logicalY += this.dirY * step;
      this.traveled += step;
      if (this.trailTimerMs >= 45) {
        pierceTrail(this.scene, this.logicalX, this.logicalY, this.rotation, 0xfff0a0);
        this.trailTimerMs = 0;
      }
      if (this.traveled > this.maxTravel) { this.kill(); return; }

      // Update visual position
      this.x = this.logicalX;
      this.y = this.logicalY;
      this.setDepth(this.logicalY + 50);

      // Hit any enemy we touch that hasn't been hit yet.
      for (const e of opts.enemiesGetter()) {
        if (!e.alive || this.hit.has(e)) continue;
        const r = e.stats.radius + 4;
        const dx = e.x - this.logicalX;
        const dy = e.y - this.logicalY;
        if (dx * dx + dy * dy <= r * r) {
          e.takeDamage(opts.damage, opts.damageType, false, opts.crit);
          this.hit.add(e);
          if (this.hit.size >= (opts.pierceCount ?? 1)) { this.kill(); return; }
        }
      }
      return;
    }

    // Homing & splash both home in on the target until impact.
    if (!opts.target || !opts.target.alive) {
      if (opts.behavior === "splash") {
        // Detonate at ground level where we are anyway, so frost/poison still get value.
        this.x = this.logicalX;
        this.y = this.logicalY;
        this.detonate();
        return;
      }
      this.kill();
      return;
    }

    const dx = opts.target.x - this.logicalX;
    const dy = opts.target.y - this.logicalY;
    const dist = Math.hypot(dx, dy);
    const step = opts.speed * dt;

    if (step >= dist) {
      this.logicalX = opts.target.x;
      this.logicalY = opts.target.y;
      this.x = opts.target.x;
      this.y = opts.target.y;
      if (opts.behavior === "splash") {
        this.detonate();
      } else {
        opts.target.takeDamage(opts.damage, opts.damageType, false, opts.crit);
        if (opts.sourceKey === "mage") magicImpact(this.scene, this.x, this.y);
        this.kill();
      }
      return;
    }

    this.logicalX += (dx / dist) * step;
    this.logicalY += (dy / dist) * step;

    // Parabolic arc height Z based on horizontal distance to target
    const progress = Math.max(0, Math.min(1, 1 - (dist / this.startDist)));
    let maxArc = 40;
    if (opts.sourceKey === "archer") {
      // Arrows are homing projectiles. A large fake ballistic arc used a
      // different visual Y coordinate from collision targeting, which made
      // arrows appear to miss and then snap onto enemies. Keep their rendered
      // path on the same line that is actually used for hit calculation.
      maxArc = 0;
    } else if (opts.sourceKey === "cannon") {
      maxArc = Math.min(140, this.startDist * 0.45);
    } else if (opts.sourceKey === "poison") {
      maxArc = Math.min(120, this.startDist * 0.4);
    } else if (opts.sourceKey === "mage" || opts.sourceKey === "holy") {
      maxArc = Math.min(50, this.startDist * 0.2);
    } else {
      maxArc = 0; // Crossbow/Tesla: flat
    }

    const height = Math.sin(progress * Math.PI) * maxArc;

    this.x = this.logicalX;
    this.y = this.logicalY - height;
    this.setDepth(this.logicalY + 50);

    // Orient sprite in flight direction (visual trajectory)
    const vx = this.x - lastX;
    const vy = this.y - lastY;
    if (Math.hypot(vx, vy) > 0.1) {
      this.rotation = Math.atan2(vy, vx);
    }
    if (opts.sourceKey === "archer" && this.trailTimerMs >= 45) {
      arrowTrail(this.scene, this.x - Math.cos(this.rotation) * 8, this.y - Math.sin(this.rotation) * 8, this.rotation);
      this.trailTimerMs = 0;
    }
  }

  private detonate(): void {
    const opts = this.opts;
    const radius = opts.splashRadius ?? 50;
    // Visual.
    if (opts.impactEffect === "explosion") {
      explosion(this.scene, this.x, this.y, radius);
      // A cannon landing is the heaviest routine event in the game — give it a
      // frame of stop so it reads as ordnance, not a pebble.
      impact.hitStop(28);
    }
    else if (opts.impactEffect === "frost") frostBurst(this.scene, this.x, this.y, radius);
    else if (opts.impactEffect === "poison") poisonCloud(this.scene, this.x, this.y, radius);

    const r2 = radius * radius;
    for (const e of opts.enemiesGetter()) {
      if (!e.alive) continue;
      const dx = e.x - this.x;
      const dy = e.y - this.y;
      if (dx * dx + dy * dy > r2) continue;
      if (opts.damage > 0) e.takeDamage(opts.damage, opts.damageType, true, opts.crit);
      if (opts.applySlow) e.applySlow(opts.applySlow.factor, opts.applySlow.durationMs);
      if (opts.applyDot)  e.applyDot(opts.applyDot.dps, opts.applyDot.durationMs, opts.sourceKey);
    }
    this.kill();
  }

  kill(): void {
    this.alive = false;
    this.destroy();
  }
}
